import { db } from "../../index";
import {useCallback, useContext, useEffect, useState, useMemo} from "react";
import { UserContext } from "../../App";
import { startLoading, stopLoading } from "../loader";

export async function createCustomer(userInfos, customer) {
  startLoading();

  customer.searchLower =
    customer.firstname.toLowerCase() + " " + customer.lastname.toLowerCase();

  let snap = await db.ref(`users/${userInfos.uid}/customer`).push(customer);
  customer.id = snap.key;

  await db
    .ref(`users/${userInfos.uid}/customer/${snap.key}`)
    .update({ id: snap.key });

  stopLoading();

  return customer;
}

export async function updateCustomer(userInfos, customer) {
  startLoading();

  customer.searchLower =
    customer.firstname.toLowerCase() + " " + customer.lastname.toLowerCase();
  await db
    .ref(`users/${userInfos.uid}/customer/${customer.id}`)
    .update(customer);

  stopLoading();

  return customer;
}

export async function deleteCustomer(userInfos, customer_id) {
  if (!Array.isArray(customer_id)) {
    customer_id = [customer_id];
  }

  startLoading();

  let pList = customer_id.map(async (id) => {
    if (id) await db.ref(`users/${userInfos.uid}/customer/${id}`).remove();
  });

  await Promise.all(pList);

  stopLoading();
}

export function useCustomers() {
  const { userInfos } = useContext(UserContext);
  const [customers, setCustomers] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // Stabilize filter reference to avoid unnecessary re-renders
  const providersFilter = useMemo(() => {
    return userInfos?.child?.rights?.providersFilter;
  }, [userInfos?.child?.rights?.providersFilter]);

  useEffect(() => {
    if (!userInfos?.uid) return;
    
    db.ref("users/" + userInfos.uid + "/customer")
      .once("value")
      .then((snapshot) => {
        let res = [];
        
        snapshot.forEach(function (childSnapshot) {
          const customer = {
            id: childSnapshot.key,
            ...childSnapshot.val(),
          };
          
          // Filter by authorized providers if user has restrictions
          if (providersFilter?.length) {
            // If customer has no provider or provider is not in authorized list, exclude it
            if (!customer.provider?.id || !providersFilter.includes(customer.provider.id)) {
              return; // Skip this customer
            }
          }
          
          res.push(customer);
        });
        
        setCustomers(res);
        setLoaded(true);
      })
      .catch((error) => {
        console.error('Error loading customers:', error);
        setLoaded(true);
      });
  }, [userInfos.uid, providersFilter]);

  return { customers, loaded };
}

async function getCustomerOptions(userInfos, providerId = null) {
  let ref = db.ref("users/" + userInfos.uid + "/customer");

  if (providerId) ref = ref.orderByChild("provider/id").equalTo(providerId);

  let res = [];

  let snapshot = await ref.once("value");
  if (!snapshot.exists()) return res;

  snapshot.forEach(function (childSnapshot) {
    const customer = childSnapshot.val();

    res.push({
      value: childSnapshot.key,
      label: customer.firstname + " " + customer.lastname,
      data: {
        ...customer,
        id: childSnapshot.key,
      },
    });
  });

  return res;
}

export function useCustomerOptionsByProvider(providerId, reloadToken = 0) {
  const { userInfos } = useContext(UserContext);
  const [customerOptions, setCustomerOptions] = useState([]);

  const getOptions = useCallback(async () =>  {
    if (providerId) {
      let res = await getCustomerOptions(userInfos, providerId);
      setCustomerOptions(res);
    }
  }, [userInfos, providerId, reloadToken]);

  useEffect(() => {
    getOptions().then();
  }, [getOptions, reloadToken]);

  return customerOptions;
}

export function useCustomerOptions(reloadToken = 0) {
  const { userInfos } = useContext(UserContext);
  const [customerOptions, setCustomerOptions] = useState([]);

  const getOptions = useCallback(async () =>  {
    let res = await getCustomerOptions(userInfos);
    setCustomerOptions(res);
  }, [userInfos, reloadToken]);

  useEffect(() => {
    getOptions().then();
  }, [getOptions, reloadToken]);

  return customerOptions;
}
